// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

// Refer to docs/implementation/qa-criteria-system.md

import React from 'react';
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Paper,
  Stack,
  Chip
} from '@mui/material';
import {
  Delete,
  DragIndicator
} from '@mui/icons-material';

/**
 * MetricEditor Component
 * 
 * Editable metric row with fields:
 * - Metric name
 * - Weight percentage
 * - Description
 * - Keywords (comma-separated)
 * - Minimum evidence items (Matrix only)
 */
const MetricEditor = ({ metric, index, onChange, onDelete, error, criteriaType = 'matrix' }) => {
  const handleChange = (field, value) => {
    onChange(index, { ...metric, [field]: value });
  };

  return (
    <Paper 
      sx={{ 
        p: 2, 
        mb: 1.5,
        border: error ? '1px solid #d32f2f' : '1px solid #e0e0e0',
        '&:hover': { 
          boxShadow: 2,
          borderColor: error ? '#d32f2f' : 'primary.main'
        }
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        {/* Drag Handle */}
        <Box sx={{ display: 'flex', alignItems: 'center', pt: 2 }}>
          <DragIndicator color="action" />
        </Box>

        {/* Metric Number */}
        <Box sx={{ minWidth: 30, pt: 2 }}>
          <Typography variant="body2" fontWeight="medium" color="text.secondary">
            #{index + 1}
          </Typography>
        </Box>

        {/* Input Fields */}
        <Box sx={{ flex: 1 }}>
          <Stack spacing={1.5}>
            {/* Metric Name */}
            <TextField
              label="Metric Name"
              value={metric.metric_name || ''}
              onChange={(e) => handleChange('metric_name', e.target.value)}
              size="small"
              fullWidth
              required
              error={!!error?.metric_name}
              helperText={error?.metric_name}
            />

            {/* Description */}
            <TextField
              label="Description"
              value={metric.description || ''}
              onChange={(e) => handleChange('description', e.target.value)}
              size="small"
              fullWidth
              multiline
              rows={2}
            />

            {/* Weight & Keywords Row */}
            <Stack direction="row" spacing={1.5}>
              {/* Weight */}
              <TextField
                label="Weight %"
                type="number"
                value={metric.weight_percentage || 0}
                onChange={(e) => handleChange('weight_percentage', parseInt(e.target.value) || 0)}
                size="small"
                sx={{ width: 120 }}
                required
                error={!!error?.weight_percentage}
                helperText={error?.weight_percentage}
                inputProps={{ min: 0, max: 100, step: 1 }}
              />

              {/* Minimum Evidence Items (Matrix only) */}
              {criteriaType === 'matrix' && (
                <TextField
                  label="Min Evidence Items"
                  type="number"
                  value={metric.min_evidence_items !== undefined ? metric.min_evidence_items : 1}
                  onChange={(e) => handleChange('min_evidence_items', parseInt(e.target.value) || 1)}
                  size="small"
                  sx={{ width: 150 }}
                  inputProps={{ min: 1, max: 10, step: 1 }}
                />
              )}

              {/* Keywords */}
              <TextField
                label="Keywords (comma-separated)"
                value={metric.keywords ? metric.keywords.join(', ') : ''}
                onChange={(e) => {
                  const keywords = e.target.value
                    .split(',')
                    .map(k => k.trim())
                    .filter(k => k);
                  handleChange('keywords', keywords);
                }}
                size="small"
                fullWidth
                placeholder="e.g., greeting, polite, customer"
              />
            </Stack>

            {/* Display Keywords as Chips */}
            {metric.keywords && metric.keywords.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {metric.keywords.map((keyword, idx) => (
                  <Chip
                    key={idx}
                    label={keyword}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            )}
          </Stack>
        </Box>

        {/* Delete Button */}
        <Box sx={{ pt: 1 }}>
          <IconButton
            onClick={() => onDelete(index)}
            color="error"
            size="small"
            title="Delete metric"
          >
            <Delete />
          </IconButton>
        </Box>
      </Stack>

      {/* Error Message */}
      {error && typeof error === 'string' && (
        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
          {error}
        </Typography>
      )}
    </Paper>
  );
};

export default MetricEditor;
